import React from 'react';
import { Link } from 'react-router-dom';
import { useDailyProcessList } from './hooks/useDailyProcessList';
import SharedFilters from '../../../components/SharedFilters';
import AppPagination from '../../../components/AppPagination';
import DailyAttendanceTable from './components/DailyAttendanceTable';
import PredefinedReportsDrawer from '../../../components/PredefinedReportsDrawer';

export default function DailyProcessListScreen() {
  const { state, actions } = useDailyProcessList();

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header Banner with Title on Left & Run Button on Right */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Daily Attendance
          </h1>
        </div>

        {/* Actions Cluster: Run Process Button */}
        <div className="flex items-center gap-3">
          <Link
            to="/payroll/daily-process/run"
            className="inline-flex items-center gap-2 rounded-2xl bg-primary px-5 py-2.5 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition-all select-none cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">published_with_changes</span>
            <span>Run Daily Process</span>
          </Link>
        </div>
      </section>

      {/* Shared Filters Component */}
      <SharedFilters
        searchField="Employee"
        searchText={state.searchText}
        setSearchText={actions.setSearchText}
        companyFilter={state.companyFilter}
        branchFilter={state.branchFilter}
        departmentFilter={state.departmentFilter}
        employeeFilter={state.employeeFilter}
        groupFilter={state.groupFilter}
        genderFilter={state.genderFilter}
        statusFilter={state.statusFilter}
        companyOptions={state.companyOptions}
        branchOptions={state.branchOptions}
        departmentOptions={state.departmentOptions}
        employeeOptions={state.employeeOptions}
        groupOptions={state.groupOptions}
        genderOptions={state.genderOptions}
        statusOptions={state.attendanceStatusOptions}
        fromDate={state.fromDate}
        toDate={state.toDate}
        showStatus={false}
        setCompanyFilter={actions.setCompanyFilter}
        setBranchFilter={actions.setBranchFilter}
        setDepartmentFilter={actions.setDepartmentFilter}
        setEmployeeFilter={actions.setEmployeeFilter}
        setGroupFilter={actions.setGroupFilter}
        setGenderFilter={actions.setGenderFilter}
        setStatusFilter={actions.setStatusFilter}
        setFromDate={actions.setFromDate}
        setToDate={actions.setToDate}
        onSearch={actions.handleSearch}
        onReset={actions.resetFilters}
        visibleAccordionFilters={[
          'company',
          'branch',
          'department',
          'employee',
          'group',
          'gender',
          'attendanceStatus',
          'fromDate',
          'toDate',
        ]}
        tools={state.tools}
        onToolSelect={actions.handleToolSelect}
      />

      {/* Table Section */}
      <section className="space-y-4">
        <DailyAttendanceTable
          records={state.records}
          isLoading={state.isLoading}
          onDelete={actions.handleDelete}
          onResetFilters={actions.resetFilters}
        />

        {/* Table Footer with Pagination */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest rounded-2xl shadow-xs">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1} to{' '}
            {Math.min(state.page * state.pageSize, state.totalCount)} of {state.totalCount} entries
          </p>
          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage((p) => Math.max(p - 1, 1))}
            onNext={() => actions.setPage((p) => Math.min(p + 1, state.totalPages))}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>

      {/* Predefined Reports Drawer */}
      <PredefinedReportsDrawer
        isOpen={state.isReportsDrawerOpen}
        onClose={() => actions.setIsReportsDrawerOpen(false)}
        title="Daily Attendance Reports"
        reports={state.predefinedReports}
        onAction={actions.handleReportAction}
      />
    </div>
  );
}
